/**
 * AI CONTEXT
 * Purpose: fetches user-related evaluation sets across multiple role fields.
 * Returns: authored (deduped), approvedBy, printedBy, and individual subsets.
 * Data source: multiple filtered evaluation subscriptions.
 * Used by: profile/evaluation ownership views.
 * Refactor: generate query configs from field list to reduce repetitive code.
 */
import { uniqBy } from "lodash"
import { useMemo } from "react"

import { evaluationAtoms } from "#atoms"
import { EvaluationConstructor } from "#customTypes/composedTypes/evaluationTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"

/**
 * Hook to fetch all evaluations associated with a user across multiple roles:
 * - assignedTo (primary authorship)
 * - savedBy, submittedBy (additional authorship)
 * - approvedBy, printedBy (separate lists)
 */
const useUserEvaluations = (userId: Uuid | undefined) => {
  // Build stable query configs for each field
  const assignedToQuery: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        assignedTo: { value: userId, comparator: COMPARATORS.EQUALS },
      },
    }),
    [userId]
  )

  const savedByQuery: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        savedBy: { value: userId, comparator: COMPARATORS.EQUALS },
      },
    }),
    [userId]
  )

  const submittedByQuery: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        submittedBy: { value: userId, comparator: COMPARATORS.EQUALS },
      },
    }),
    [userId]
  )

  const approvedByQuery: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        approvedBy: { value: userId, comparator: COMPARATORS.EQUALS },
      },
    }),
    [userId]
  )

  const printedByQuery: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        printedBy: { value: userId, comparator: COMPARATORS.EQUALS },
      },
    }),
    [userId]
  )

  // Subscribe to each query
  const assignedToEvals = useFilteredDocs<EvaluationConstructor>(
    evaluationAtoms,
    DOC_TYPES.EVALUATIONS,
    assignedToQuery
  )

  const savedByEvals = useFilteredDocs<EvaluationConstructor>(
    evaluationAtoms,
    DOC_TYPES.EVALUATIONS,
    savedByQuery
  )

  const submittedByEvals = useFilteredDocs<EvaluationConstructor>(
    evaluationAtoms,
    DOC_TYPES.EVALUATIONS,
    submittedByQuery
  )

  const approvedByEvals = useFilteredDocs<EvaluationConstructor>(
    evaluationAtoms,
    DOC_TYPES.EVALUATIONS,
    approvedByQuery
  )

  const printedByEvals = useFilteredDocs<EvaluationConstructor>(
    evaluationAtoms,
    DOC_TYPES.EVALUATIONS,
    printedByQuery
  )

  // Combine authorship evals (assignedTo + savedBy + submittedBy) with deduplication
  const authoredEvals = useMemo(() => {
    return uniqBy(
      [...assignedToEvals, ...savedByEvals, ...submittedByEvals],
      "id"
    )
  }, [assignedToEvals, savedByEvals, submittedByEvals])

  return {
    authoredEvals,
    approvedByEvals,
    printedByEvals,
    // Expose individual queries if needed
    assignedToEvals,
    savedByEvals,
    submittedByEvals,
  }
}

export default useUserEvaluations
